import React, { useEffect, useRef, useState } from 'react'
import { Input, Button, FormItem, FormContainer, Alert } from 'components/ui'
import { PasswordInput, ActionLink } from 'components/shared'
import useTimeOutMessage from 'utils/hooks/useTimeOutMessage'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import useAuth from 'utils/hooks/useAuth'
import ReCAPTCHA from 'react-google-recaptcha'
import { useDispatch } from 'react-redux'
import {
    setAuthPanelLoginExpand,
    setAuthPanelSignupExpand,
} from 'store/theme/themeSlice'
import VerifyForm from './VerifyForm'
import { useLocation } from 'react-router-dom'

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Please enter your name'),
    email: Yup.string()
        .email('Invalid email')
        .required('Please enter your email'),
    password: Yup.string().min(8).required('Please enter your password'),
    confirmPassword: Yup.string().oneOf(
        [Yup.ref('password'), null],
        'Your passwords do not match'
    ),
})

const SignUpForm = (props) => {
    const { disableSubmit = false, className } = props
    const dispatch = useDispatch()
    const location = useLocation()

    const recaptchaRef = useRef(null)

    const { signUp } = useAuth()

    const [showVerifyOtp, setShowVerifyOtp] = useState(false)
    const [message, setMessage] = useTimeOutMessage(10000)
    const [email, setEmail] = useState('')

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search)
        const withParam = searchParams.get('_with')
        const emailParam = searchParams.get('email')
        if (withParam === 'verify' && emailParam) {
            setEmail(emailParam)
            setShowVerifyOtp(true)
        }
    }, [location])

    const onSignUp = async (values, setSubmitting, setErrors) => {
        const { name, password, confirmPassword, email } = values
        setEmail(email)
        setSubmitting(true)
        const recaptchaToken = await recaptchaRef.current.executeAsync()

        const result = await signUp({
            name,
            password,
            password_confirmation: confirmPassword,
            email,
            recaptchaToken,
        })
        if (result.status === 'failed') {
            setMessage(result.message)
            result?.errors && setErrors(result.errors)
        } else {
            setShowVerifyOtp(true)
        }

        setSubmitting(false)
    }

    return showVerifyOtp ? (
        <>
            <div className="mb-6 mt-3">
                <p>
                    Please enter the One-Time Password (OTP) sent to your
                    registered email address in the form below to proceed. If
                    you did not receive the OTP, you can request a new one using
                    the 'Resend OTP' option.
                </p>
            </div>
            <VerifyForm email={email} />
        </>
    ) : (
        <>
            <div className="mb-6 mt-3">
                <p>
                    Complete the account creation form below for free, instant
                    access, to a new world for premium licensed content, along
                    with collections of free-to-use high quality images,
                    articles, videos and audio content.
                </p>
            </div>
            <div className={className}>
                {message && (
                    <Alert className="mb-4" type="danger" showIcon>
                        {message}
                    </Alert>
                )}
                <Formik
                    initialValues={{
                        name: '',
                        password: '',
                        confirmPassword: '',
                        email: '',
                    }}
                    validationSchema={validationSchema}
                    onSubmit={(values, { setSubmitting, setErrors }) => {
                        if (!disableSubmit) {
                            onSignUp(values, setSubmitting, setErrors)
                        } else {
                            setSubmitting(false)
                        }
                    }}
                >
                    {({ touched, errors, isSubmitting }) => (
                        <Form>
                            <FormContainer>
                                <FormItem
                                    label="Name"
                                    invalid={errors.name && touched.name}
                                    errorMessage={errors.name}
                                >
                                    <Field
                                        type="text"
                                        autoComplete="off"
                                        name="name"
                                        placeholder="Name"
                                        component={Input}
                                    />
                                </FormItem>
                                <FormItem
                                    label="Email"
                                    invalid={errors.email && touched.email}
                                    errorMessage={errors.email}
                                >
                                    <Field
                                        type="email"
                                        autoComplete="off"
                                        name="email"
                                        placeholder="Email"
                                        component={Input}
                                    />
                                </FormItem>
                                <FormItem
                                    label="Password"
                                    invalid={
                                        errors.password && touched.password
                                    }
                                    errorMessage={errors.password}
                                >
                                    <Field
                                        autoComplete="off"
                                        name="password"
                                        placeholder="Password"
                                        component={PasswordInput}
                                    />
                                </FormItem>
                                <FormItem
                                    label="Confirm Password"
                                    invalid={
                                        errors.confirmPassword &&
                                        touched.confirmPassword
                                    }
                                    errorMessage={errors.confirmPassword}
                                >
                                    <Field
                                        autoComplete="off"
                                        name="confirmPassword"
                                        placeholder="Confirm Password"
                                        component={PasswordInput}
                                    />
                                </FormItem>
                                <div className="hidden">
                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        size="invisible"
                                        sitekey="6LfHNskUAAAAALXWeqW6kUjlmcU_r6zHHTHXLRay"
                                    />
                                </div>
                                <Button
                                    block
                                    loading={isSubmitting}
                                    variant="solid"
                                    type="submit"
                                >
                                    {isSubmitting
                                        ? 'Creating Account...'
                                        : 'Sign Up'}
                                </Button>
                                <div className="mt-4 text-center">
                                    <span>Already have an account? </span>
                                    <span
                                        className="text-primary cursor-pointer"
                                        onClick={() => {
                                            dispatch(
                                                setAuthPanelSignupExpand(false)
                                            )
                                            dispatch(
                                                setAuthPanelLoginExpand(true)
                                            )
                                        }}
                                    >
                                        Sign In
                                    </span>
                                </div>
                            </FormContainer>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    )
}

export default SignUpForm
